<script setup lang="ts">
import { Info } from '@/@types/info'
import logo from '@/assets/logo-forum-dark.svg'
import AccessData from '@/components/shared/users/registration/AccessData.vue'
import PersonalData from '@/components/shared/users/registration/PersonalData.vue'
import { Button } from '@/components/ui/button'
import { StepperIndicator, StepperItem, StepperTitle } from '@/components/ui/stepper'
import Stepper from '@/components/ui/stepper/Stepper.vue'
import { Title } from '@/components/ui/title'
import AuthLayout from '@/layouts/AuthLayout.vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js/max'
import { Check, Loader2 } from 'lucide-vue-next'
import { computed, provide, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { z, ZodIssue } from 'zod'
const { t } = useI18n()

const props = defineProps<{
    billing_setor: Info[]
    billing_area: Info[]
    billing_nivel_hierarquico: Info[]
}>()

const form = useForm({
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    billing_linkedin: '',
    billing_empresa: '',
    billing_setor: '',
    billing_nivel_hierarquico: '',
    billing_area: '',
    phone: '',
    password: '',
    password_confirmation: '',
})

export type FormSchema = typeof form

const passwordErrors = ref<ZodIssue[]>([])

const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/

async function checkEmailAvailability(email: string) {
    const response = await fetch(`/users/check_email?email=${encodeURIComponent(email)}`)
    return (await response.json()) as { available?: boolean; message?: string }
}

const emailFieldSchema = z
    .string()
    .min(1, { message: t('Required') })
    .email({ message: t('Invalid email') })

/** Passo 1: validação síncrona de todos os campos (incluindo telefone) ao mesmo tempo. */
const firstStepFields = z.object({
    first_name: z
        .string()
        .min(1, { message: t('Required') })
        .max(150, { message: t('Max 150 characters') })
        .regex(nameRegex, { message: t('Special characters are not allowed') }),
    last_name: z
        .string()
        .min(1, { message: t('Required') })
        .max(150, { message: t('Max 150 characters') })
        .regex(nameRegex, { message: t('Special characters are not allowed') }),
    billing_empresa: z.string().min(1, { message: t('Required') }),
    billing_linkedin: z
        .string()
        .optional()
        .refine((value) => !value || /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})(\/[\w.-]*)*\/?$/.test(value), {
            message: t('Invalid URL'),
        }),
    email: emailFieldSchema,
    phone: z
        .string()
        .trim()
        .min(1, { message: t('Required') })
        .refine(
            (value) => {
                const parsed = parsePhoneNumberFromString(value) ?? parsePhoneNumberFromString(value, 'BR')
                return parsed !== undefined && parsed.isValid()
            },
            { message: t('Invalid phone number') },
        ),
    billing_setor: z.string().min(1, { message: t('Required') }),
    billing_nivel_hierarquico: z.string().min(1, { message: t('Required') }),
    billing_area: z.string().min(1, { message: t('Required') }),
})

const secondStep = z
    .object({
        password: z
            .string()
            .min(6, { message: t('At least 6 characters') })
            .regex(/[^a-zA-Z0-9 ]/, { message: t('At least 1 special character') })
            .regex(/[0-9]/, { message: t('At least 1 number') })
            .regex(/[a-z]/, { message: t('At least 1 lowercase letter') })
            .regex(/[A-Z]/, { message: t('At least 1 uppercase letter') }),
        password_confirmation: z.string().min(1, { message: t('Required') }),
    })
    .superRefine((data, ctx) => {
        if (data.password !== data.password_confirmation) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('Passwords do not match'),
                path: ['password_confirmation'],
            })
        }
    })

async function handleSubmit() {
    form.clearErrors()

    if (currentStep.value === 1) {
        const syncResult = firstStepFields.safeParse(form.data())
        const issues = syncResult.success ? [] : [...syncResult.error.issues]

        if (emailFieldSchema.safeParse(form.email).success) {
            const payload = await checkEmailAvailability(form.email)
            if (!payload.available) {
                issues.push({
                    code: z.ZodIssueCode.custom,
                    message: payload.message ?? t('Email has already been taken'),
                    path: ['email'],
                })
            }
        }

        if (issues.length > 0) {
            const { fieldErrors } = new z.ZodError(issues).flatten()
            const formData = form.data()
            for (const [key, messages] of Object.entries(fieldErrors)) {
                if (!messages?.length) continue
                if (!(key in formData)) continue
                form.setError(key as keyof typeof formData, messages.join(' '))
            }
            return
        }

        return currentStep.value++
    }

    if (currentStep.value === 2) {
        const result = secondStep.safeParse(form.data())

        if (!result.success) {
            passwordErrors.value = result.error.errors.filter((error) => error.path[0] === 'password')

            return result.error.errors.forEach((error) => {
                if (error.path[0] === 'password') return

                const formData = form.data()
                const field = error.path[0] as keyof typeof formData

                form.setError(field, error.message)
            })
        }

        return form
            .transform((data) => ({
                user: {
                    ...data,
                    name: `${data.first_name} ${data.last_name}`.trim(),
                },
            }))
            .post('/users')
    }
}

const steps = [
    {
        step: 1,
        title: 'Personal data',
        component: PersonalData,
    },
    {
        step: 2,
        title: 'Access data',
        component: AccessData,
    },
]

type FormFields = 'first_name' | 'last_name' | 'email' | 'billing_empresa' | 'phone' | 'billing_setor' | 'billing_nivel_hierarquico' | 'billing_area'

const currentStep = ref(1)

provide('billing_setor', props.billing_setor)
provide('billing_area', props.billing_area)
provide('billing_nivel_hierarquico', props.billing_nivel_hierarquico)

const isFormValid = computed(() => {
    const requiredFields: FormFields[] = [
        'first_name',
        'last_name',
        'email',
        'billing_empresa',
        'phone',
        'billing_setor',
        'billing_nivel_hierarquico',
        'billing_area',
    ]

    return requiredFields.every((field) => form[field] && form[field].trim() !== '')
})
</script>

<template>
    <Head title="Login" />

    <AuthLayout>
        <div class="flex items-center justify-center">
            <img :src="logo" alt="" class="pr-4" width="150" />
        </div>
        <div class="mt-8 w-full">
            <Title>{{ t('Create account') }}</Title>

            <p class="mt-3 text-sm font-medium">
                {{ $t('Log in with your ILOS username, the same one you use to access ILOS Prime or ILOS Courses') }}
            </p>

            <p class="mt-3 text-sm font-medium">
                {{ $t('Have account?') }}
                <Link href="/users/sign_in" class="font-inter text-primary self-end font-semibold hover:underline">{{ $t('Log in') }}</Link>
            </p>

            <form @submit.prevent="handleSubmit()" id="register" class="mt-4 flex flex-col gap-4">
                <Stepper v-model="currentStep" class="font-inter flex w-full items-start gap-2 overflow-x-auto">
                    <StepperItem v-for="step in steps" :key="step.step" v-slot="{ state }" class="flex w-fit px-2 first:pl-0" :step="step.step">
                        <StepperIndicator
                            :disabled="state != 'completed'"
                            class="group-data-[state=completed]:bg-primary group-data-[state=active]:bg-tranparent group-data-[state=active]:border-primary group-data-[state=active]:text-primary h-[40px] w-[40px] group-data-[state=active]:border group-data-[state=active]:border-dashed group-data-[state=completed]:text-white group-data-[state=inactive]:border"
                        >
                            <Check v-if="state === 'completed'" />
                            <span v-if="state != 'completed'">{{ String(step.step).padStart(2, '0') }}</span>
                        </StepperIndicator>
                        <StepperTitle
                            as="button"
                            type="button"
                            class="group-data-[state=active]:text-primary text-sm font-medium text-gray-900 group-data-[state=inactive]:text-slate-400"
                        >
                            {{ $t(step.title) }}
                        </StepperTitle>
                    </StepperItem>
                </Stepper>

                <component :is="steps[currentStep - 1].component" :form="form" :passwordErrors="passwordErrors" />
            </form>

            <p class="mt-4 text-sm font-medium">
                {{ $t('By submitting this form, you declare that you have read and agree to our') }}
                <a href="/privacy-policy#privacy">{{ $t('Privacy Policy') }}</a
                >.
            </p>

            <div class="mt-8 flex w-full items-center justify-between">
                <Button v-if="currentStep > 1" @click="currentStep--" variant="outline">{{ $t('Back') }}</Button>
                <Button v-if="currentStep < steps.length" type="submit" form="register" class="w-full" :disabled="!isFormValid">{{
                    $t('Next')
                }}</Button>
                <Button v-if="currentStep === steps.length" type="submit" form="register" :disabled="form.processing" class="grid place-items-center">
                    <span v-if="!form.processing" class="col-start-1 row-start-1"> {{ $t('Register') }} </span>
                    <span v-if="form.processing" class="col-start-1 row-start-1"> <Loader2 class="animate-spin" /> </span>
                </Button>
            </div>
        </div>
    </AuthLayout>
</template>
